import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

export const getMantisResponse = async (
  prompt,
  files = [],
  systemInstructions = ""
) => {
  try {
    const response = await axiosWithAuth.post(
      `${API_URL}/businessFlows/ia/generate`,
      {
        prompt,
        files,
        system_instructions: systemInstructions,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return {
      response: response.data.response,
      tokenCount: response.data.token_count,
      cost: response.data.cost,
      files: response.data.files,
    };
  } catch (error) {
    console.error("Error al comunicarse con Mantis IA:", error);
    throw error;
  }
};

// ===== FUNCIONES PARA INSIGHTS DE IA EN IPR =====

// Obtener insights de IA para un IPR específico
export const getIPRInsights = async (clientId, iprId) => {
  try {
    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/iprs/${iprId}/ia-insights`);
    return response.data;
  } catch (error) {
    console.error("Error al obtener insights del IPR:", error);
    
    // Si es un error 404, significa que no hay insights guardados
    if (error.response?.status === 404) {
      return null;
    }
    
    throw error;
  }
};

// Actualizar/guardar insights de IA para un IPR específico
export const updateIPRInsights = async (clientId, iprId, insights) => {
  try {
    const response = await axiosWithAuth.put(
      `${API_URL}/workflows/p2p/${clientId}/iprs/${iprId}/ia-insights`,
      {
        insights: insights,
        generated_at: new Date().toISOString()
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al actualizar insights del IPR:", error);
    throw error;
  }
};

// Obtener insights de múltiples IPRs
export const getMultipleIPRInsights = async (clientId, iprIds) => {
  try {
    const response = await axiosWithAuth.post(
      `${API_URL}/workflows/p2p/${clientId}/iprs/ia-insights/bulk`,
      {
        ipr_ids: iprIds
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener insights múltiples:", error);
    throw error;
  }
};

// Limpiar insights de IA para un IPR específico
export const clearIPRInsights = async (clientId, iprId) => {
  try {
    const response = await axiosWithAuth.delete(`${API_URL}/workflows/p2p/${clientId}/iprs/${iprId}/ia-insights`);
    return response.data;
  } catch (error) {
    console.error("Error al limpiar insights del IPR:", error);
    throw error;
  }
};

// Función helper para generar y guardar insights automáticamente
export const generateAndSaveIPRInsights = async (clientId, iprId, iprData, orderItems = []) => {
  try {
    // Generar prompt basado en los datos
    const prompt = generateInsightsPrompt(iprData, orderItems);
    
    // Llamar a la IA para generar insights
    const aiResponse = await getMantisResponse(
      prompt, 
      [], 
      "Eres un analista financiero y de procesos experto especializado en gestión de inventarios. Analiza la información proporcionada, especialmente el estado del stock, y genera insights precisos y útiles en formato JSON. Incluye sugerencias de items alternativos cuando sea necesario."
    );
    
    // Limpiar y parsear la respuesta
    const cleanResponse = cleanAIResponse(aiResponse.response);
    const parsedInsights = JSON.parse(cleanResponse);
    
    // Guardar los insights en la base de datos
    await updateIPRInsights(clientId, iprId, parsedInsights);
    
    return parsedInsights;
  } catch (error) {
    console.error("Error al generar y guardar insights:", error);
    throw error;
  }
};

// Función para limpiar respuestas complejas de la IA
const cleanAIResponse = (response) => {
  let cleaned = response;
  
  // Remover prefijos y sufijos comunes
  cleaned = cleaned.replace(/```json/g, '').replace(/```/g, '');
  
  // Caso especial: Respuesta como string concatenado de JavaScript
  if (cleaned.includes("'\\n' +") || cleaned.includes("` +")) {
    // Convertir las concatenaciones de JavaScript en un string continuo
    cleaned = cleaned.replace(/'\\n'\s*\+\s*/g, '');
    cleaned = cleaned.replace(/`\s*\+\s*/g, '');
    cleaned = cleaned.replace(/\s*\+\s*`/g, '');
    cleaned = cleaned.replace(/'\s*\+\s*/g, '');
    cleaned = cleaned.replace(/\s*\+\s*'/g, '');
    
    // Remover comillas al inicio y final
    cleaned = cleaned.replace(/^[`'"]|[`'"]$/g, '');
  }
  
  // Buscar el contenido JSON válido
  let jsonStart = cleaned.indexOf('{');
  let jsonEnd = cleaned.lastIndexOf('}');
  
  // Si no encuentra llaves, buscar en el contenido limpio
  if (jsonStart === -1) {
    // Remover caracteres no deseados y buscar de nuevo
    cleaned = cleaned.replace(/[\n\r\t]/g, '').replace(/\s+/g, ' ');
    jsonStart = cleaned.indexOf('{');
    jsonEnd = cleaned.lastIndexOf('}');
  }
  
  if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
    cleaned = cleaned.substring(jsonStart, jsonEnd + 1);
  }
  
  // Limpiar caracteres de escape y formato
  cleaned = cleaned.replace(/\\n/g, '');
  cleaned = cleaned.replace(/\\"/g, '"');
  cleaned = cleaned.replace(/\\\\/g, '\\');
  
  // Normalizar espacios
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

// Función para generar prompt basado en los datos del IPR y los items
const generateInsightsPrompt = (iprData, items) => {
  // Procesar items para incluir análisis de stock
  const itemsAnalysis = items.map(item => {
    const currentStock = parseInt(item.item_stock || 0);
    const quantityToOrder = parseInt(item.quantity_to_order || 0);
    const stockStatus = currentStock >= quantityToOrder ? 'sufficient' : 
                       currentStock > 0 ? 'partial' : 'insufficient';
    
    return {
      name: item.name || 'Sin nombre',
      sku: item.item_sku || 'N/A',
      category: item.item_category_name || 'Sin categoría',
      quantityToOrder,
      currentStock,
      stockStatus,
      shortfall: stockStatus !== 'sufficient' ? quantityToOrder - currentStock : 0
    };
  });

  const itemsWithoutStock = itemsAnalysis.filter(item => item.stockStatus !== 'sufficient');
  const totalItems = itemsAnalysis.length;
  const itemsWithSufficientStock = itemsAnalysis.filter(item => item.stockStatus === 'sufficient').length;

  return `
Analiza los siguientes datos de una Solicitud de Compra (IPR) y genera un análisis completo en formato JSON:

DATOS DEL IPR:
- ID: ${iprData?.id || 'N/A'}
- Título: ${iprData?.title || 'Sin título'}
- Monto Total: ${iprData?.total_amount || '$0.00'}
- Categoría: ${iprData?.category || 'No especificada'}
- Subcategoría: ${iprData?.subcategory || 'No especificada'}
- Departamento: ${iprData?.area || iprData?.department || 'No especificado'}
- Estado: ${iprData?.status || 'pendiente'}
- Criticidad: ${iprData?.criticality || 'baja'}
- Fecha de Entrega: ${iprData?.delivery_target_date || 'No establecida'}
- Solicitante: ${iprData?.created_by_name || 'Desconocido'}
- Fecha de Creación: ${iprData?.created_at || 'No disponible'}

ANÁLISIS DE ITEMS Y STOCK:
- Total de items: ${totalItems}
- Items con stock suficiente: ${itemsWithSufficientStock}
- Items sin stock suficiente: ${itemsWithoutStock.length}

ITEMS SIN STOCK SUFICIENTE:
${itemsWithoutStock.map(item => `
  * ${item.name} (SKU: ${item.sku})
    - Categoría: ${item.category}
    - Cantidad solicitada: ${item.quantityToOrder}
    - Stock actual: ${item.currentStock}
    - Faltante: ${item.shortfall} unidades
    - Estado: ${item.stockStatus === 'partial' ? 'Stock parcial' : 'Sin stock'}
`).join('')}

CATEGORÍAS DE ITEMS:
${[...new Set(itemsAnalysis.map(item => item.category))].map(category => `
  * ${category}: ${itemsAnalysis.filter(item => item.category === category).length} items
`).join('')}

INSTRUCCIONES:
Genera un análisis en formato JSON con la siguiente estructura exacta:

{
  "summary": {
    "title": "Resumen Ejecutivo",
    "content": "Un resumen de 2-3 líneas sobre la solicitud incluyendo estado del stock",
    "status": "success|warning|error",
    "recommendations": ["recomendación 1", "recomendación 2"]
  },
  "stock_analysis": {
    "title": "Análisis de Stock e Inventario",
    "status": "HIGH|MEDIUM|LOW",
    "items_ready": ${itemsWithSufficientStock},
    "items_pending": ${itemsWithoutStock.length},
    "completion_percentage": ${totalItems > 0 ? Math.round((itemsWithSufficientStock / totalItems) * 100) : 0},
    "insights": [
      "insight sobre disponibilidad de stock",
      "insight sobre items faltantes",
      "insight sobre impacto en el cronograma"
    ],
    "alternative_suggestions": [
      "sugerencia de items alternativos para los faltantes",
      "recomendación de proveedores alternativos",
      "estrategia de sustitución de productos"
    ],
    "recommendation": "Recomendación específica para gestión de stock"
  },
  "budget_analysis": {
    "title": "Análisis Presupuestal",
    "status": "HIGH|MEDIUM|LOW",
    "budget_usage": 60,
    "remaining_budget": "1.200.000",
    "insights": [
      "insight sobre utilización presupuestal",
      "insight sobre impacto financiero",
      "insight sobre riesgos presupuestales"
    ],
    "recommendation": "Recomendación específica para el presupuesto"
  },
  "cash_flow_analysis": {
    "title": "Análisis de Flujo de Caja",
    "status": "HIGH|MEDIUM|LOW",
    "impact": "Alto|Medio|Bajo",
    "payment_terms": "Términos de pago sugeridos",
    "insights": [
      "insight sobre liquidez",
      "insight sobre impacto en flujo de caja",
      "insight sobre términos de pago"
    ],
    "recommendation": "Recomendación para flujo de caja"
  },
  "compliance_analysis": {
    "title": "Cumplimiento de Políticas",
    "status": "HIGH|MEDIUM|LOW",
    "compliance_score": 100,
    "insights": [
      "insight sobre cumplimiento",
      "insight sobre políticas aplicables",
      "insight sobre riesgos de cumplimiento"
    ]
  },
  "approval_flow": {
    "title": "Flujo de Aprobaciones",
    "estimated_time": "2-3 días hábiles",
    "approvers": [
      {"name": "Ana García", "initials": "AG"},
      {"name": "Carlos López", "initials": "CL"}
    ],
    "insights": [
      "insight sobre el proceso de aprobación",
      "insight sobre posibles cuellos de botella",
      "insight sobre eficiencia del flujo"
    ]
  }
}

IMPORTANTE: 
- Responde ÚNICAMENTE con el JSON válido, sin texto adicional
- NO uses formato markdown (sin \`\`\`)
- NO incluyas explicaciones antes o después del JSON
- Analiza los datos reales proporcionados, especialmente el estado del stock
- Para items sin stock suficiente, sugiere alternativas específicas y viables
- Genera insights relevantes y específicos para esta solicitud
- Los valores numéricos deben ser realistas basados en los datos
- Las recomendaciones deben ser accionables y específicas
- Incluye sugerencias de items alternativos cuando sea apropiado
- La respuesta debe empezar directamente con { y terminar con }
`;
};
